<script setup lang="ts">
import type { CmsHeadlineFont } from '@mop/cms/types';
type HeadlineLevel = 0 | 1 | 2 | 3 | 4 | 5 | 6;

defineOptions({
  name: 'UiHeading',
});
const props = defineProps({
  font: {
    type: String as PropType<CmsHeadlineFont>,
    default: 'h2',
  },
  isUppercase: {
    type: Boolean,
    default: false,
  },
  level: {
    type: Number as PropType<HeadlineLevel>,
    default: undefined,
  },
});
const levelFromFont: HeadlineLevel = parseInt(props.font.split(' ')?.[0]?.match(/\d+$/)?.[0] ?? '1') as HeadlineLevel;
</script>

<template>
  <UiHeading
    v-if="$slots.default?.()"
    :class="[
      'ui-heading',
      `ui-heading--${font}`,
      {
        'ui-heading--uppercase': isUppercase,
      },
    ]"
    :level="level ?? levelFromFont"
  >
    <slot />
  </UiHeading>
</template>

<style lang="scss" scoped>
.ui-heading {
  color: var(--color);
}

.ui-heading--h1 {
  @include v2-text-style('5xl', 'highlight');
  @include v2-apply-upto('tablet') {
    @include v2-text-style('4xl', 'highlight');
  }
}

.ui-heading--h2 {
  @include v2-text-style('4xl', 'highlight');
  @include v2-apply-upto('tablet') {
    @include v2-text-style('2xl', 'highlight');
  }
}

.ui-heading--h3 {
  @include v2-text-style('2xl', 'highlight');
  @include v2-apply-upto('tablet') {
    @include v2-text-style('xl', 'highlight');
  }
}

.ui-heading--h1-serif {
  @include v2-text-style('5xl');
  @include v2-font-family('serif');
  @include v2-apply-upto('tablet') {
    @include v2-text-style('4xl');
    @include v2-font-family('serif');
  }
}

.ui-heading--h2-serif {
  @include v2-text-style('4xl');
  @include v2-font-family('serif');
  @include v2-apply-upto('tablet') {
    @include v2-text-style('2xl');
    @include v2-font-family('serif');
  }
}

.ui-heading--h3-serif {
  @include v2-text-style('2xl');
  @include v2-font-family('serif');
  @include v2-apply-upto('tablet') {
    @include v2-text-style('xl');
    @include v2-font-family('serif');
  }
}

.ui-heading--uppercase {
  text-transform: uppercase;
}
</style>
